import {createContext, useContext, useCallback, useEffect, useMemo, useState} from 'react';
import {useAuth} from '../../../hooks/useAuth';
import useCampaignRuntime from '../hooks/useCampaignRuntime';
import {useAgentChatContext} from './AgentChatContext';
import useAgentSessionStore from '../store/agentSessionStore';
import {
  buildInitialScheduleEntryFromArt,
  buildScheduleFieldsFromArt,
  getArtScheduleKey,
  isArtScheduleCommitted,
  isCompleteScheduleDateTimeFields,
  scheduleFieldsDifferFromCanonical,
} from '../../../shared/campaign/utils/scheduleDateTime';
import {
  normalizeSchedulePreviewArt,
  resolveScheduleUxState,
} from '../domain/campaignStateSelectors';

const CampaignRuntimeContext = createContext(null);

function getScheduleStorageKey(sessionId) {
  return `campaign_schedule_${sessionId}`;
}

function loadScheduleFromStorage(sessionId) {
  if (!sessionId) return null;
  try {
    const raw = localStorage.getItem(getScheduleStorageKey(sessionId));
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function saveScheduleToStorage(sessionId, data) {
  if (!sessionId) return;
  try {
    localStorage.setItem(getScheduleStorageKey(sessionId), JSON.stringify(data));
  } catch {
    // ignore
  }
}

function buildSocialScheduleContextFromUserInput(userInput) {
  if (!userInput || typeof userInput !== 'object') {
    return null;
  }

  const socialSelection = Array.isArray(userInput.socialSelection)
    ? userInput.socialSelection
    : [];
  const socialNetworks = Array.isArray(userInput.socialNetworks)
    ? userInput.socialNetworks
    : [];

  if (!socialSelection.length && !socialNetworks.length) {
    return null;
  }

  const socialMediaSettings = socialSelection.reduce((acc, item) => {
    if (!item?.name) {
      return acc;
    }
    acc[String(item.name).trim().toLowerCase()] = item.data || {};
    return acc;
  }, {});

  return {
    selectedNetworks: socialNetworks,
    socialMediaSettings,
    socialSelection,
  };
}

export function CampaignRuntimeProvider({children}) {
  const {userData} = useAuth();
  const userAccountId = userData?.userAccount?.[0]?.uuid;
  const activeSessionId = useAgentSessionStore((s) => s.activeSessionId);
  const {sessionDetail, messages, status, refetch} = useAgentChatContext();

  const [pendingSubmissions, setPendingSubmissions] = useState({});
  const [schedulePreviewRequest, setSchedulePreviewRequest] = useState(null);
  const [hasSelectedSchedulePublicationItem, setHasSelectedSchedulePublicationItem] =
    useState(false);
  const [socialScheduleContext, setSocialScheduleContext] = useState({
    selectedNetworks: [],
    socialMediaSettings: {},
    socialSelection: [],
  });
  const [artScheduleData, setArtScheduleData] = useState(() => {
    return loadScheduleFromStorage(activeSessionId) || {};
  });

  const runtimeFallback = useMemo(
    () => ({
      content: {artStatus: 'idle'},
      liveOverlay: {lastInvalidationRevision: null},
    }),
    [],
  );

  const campaignRuntime = useCampaignRuntime({
    userAccountId,
    activeSessionId,
    sessionDetail,
    transcript: messages,
    pendingSubmissions,
    chatTransportStatus: status,
  });

  const {
    runtime: campaignRuntimeState = runtimeFallback,
    viewModel,
    resetCampaignRuntime,
    ...socketRuntime
  } = campaignRuntime ?? {};

  useEffect(() => {
    if (!campaignRuntimeState?.liveOverlay?.lastInvalidationRevision) {
      return;
    }
    void refetch();
  }, [refetch, campaignRuntimeState?.liveOverlay?.lastInvalidationRevision]);

  useEffect(() => {
    saveScheduleToStorage(activeSessionId, artScheduleData);
  }, [activeSessionId, artScheduleData]);

  useEffect(() => {
    const stored = loadScheduleFromStorage(activeSessionId);
    setArtScheduleData(stored || {});
    const hydratedSocial = buildSocialScheduleContextFromUserInput(
      sessionDetail?.userInput,
    );
    setSocialScheduleContext(
      hydratedSocial || {
        selectedNetworks: [],
        socialMediaSettings: {},
        socialSelection: [],
      },
    );
    setHasSelectedSchedulePublicationItem(false);
    setPendingSubmissions({});
  }, [activeSessionId, sessionDetail?.userInput]);

  useEffect(() => {
    if (!socialScheduleContext.selectedNetworks?.length &&
        !socialScheduleContext.socialSelection?.length) {
      setHasSelectedSchedulePublicationItem(false);
    }
  }, [
    socialScheduleContext.selectedNetworks,
    socialScheduleContext.socialSelection,
  ]);

  useEffect(() => {
    if (status !== 'ready') {
      return;
    }
    setPendingSubmissions((prev) => {
      const next = {...prev};
      let changed = false;
      Object.keys(prev).forEach((trackingId) => {
        const part = messages
          .flatMap((msg) => msg.parts || [])
          .find(
            (item) =>
              item.toolCallId === trackingId &&
              (item.output?.status === 'completed' ||
                item.output?.status === 'error'),
          );
        if (part) {
          delete next[trackingId];
          changed = true;
        }
      });
      return changed ? next : prev;
    });
  }, [messages, status]);

  useEffect(() => {
    if (campaignRuntimeState?.content?.artStatus === 'generated') {
      window.dispatchEvent(new CustomEvent('user-plan-count-updated'));
    }
  }, [campaignRuntimeState?.content?.artStatus]);

  const registerPendingSubmission = useCallback((trackingId) => {
    if (!trackingId) {
      return;
    }
    setPendingSubmissions((prev) => ({...prev, [trackingId]: true}));
  }, []);

  const clearPendingSubmission = useCallback((trackingId) => {
    if (!trackingId) {
      return;
    }
    setPendingSubmissions((prev) => {
      if (!prev[trackingId]) {
        return prev;
      }
      const next = {...prev};
      delete next[trackingId];
      return next;
    });
  }, []);

  const resetArtScheduleData = useCallback(() => {
    setArtScheduleData({});
    setHasSelectedSchedulePublicationItem(false);
    setSocialScheduleContext({
      selectedNetworks: [],
      socialMediaSettings: {},
      socialSelection: [],
    });
  }, []);

  const ensureArtScheduleEntry = useCallback((art, index = 0) => {
    const artKey = getArtScheduleKey(art, index);
    setArtScheduleData((prev) => {
      const existing = prev[artKey];
      const artCaption = buildInitialScheduleEntryFromArt(art);

      if (isArtScheduleCommitted(art)) {
        const canonical = buildScheduleFieldsFromArt(art);
        const hasDraftSchedule =
          isCompleteScheduleDateTimeFields(
            existing?.publishDate,
            existing?.publishTime,
          ) && scheduleFieldsDifferFromCanonical(existing, art);

        return {
          ...prev,
          [artKey]: {
            ...artCaption,
            ...existing,
            publishDate: hasDraftSchedule
              ? existing.publishDate
              : canonical.publishDate,
            publishTime: hasDraftSchedule
              ? existing.publishTime
              : canonical.publishTime,
            publicationStatus: canonical.publicationStatus || existing?.publicationStatus,
            caption:
              existing?.caption !== undefined
                ? existing.caption
                : artCaption.caption,
            selectedNetworks:
              existing?.selectedNetworks?.length
                ? existing.selectedNetworks
                : artCaption.selectedNetworks,
          },
        };
      }

      if (!existing) {
        return {...prev, [artKey]: artCaption};
      }
      const updates = {};
      if (!existing.caption && artCaption.caption) {
        updates.caption = artCaption.caption;
      }
      if (
        !existing.publishDate &&
        !existing.publishTime &&
        (artCaption.publishDate || artCaption.publishTime)
      ) {
        updates.publishDate = artCaption.publishDate;
        updates.publishTime = artCaption.publishTime;
      }
      if (
        (!existing.selectedNetworks || existing.selectedNetworks.length === 0) &&
        (artCaption.selectedNetworks && artCaption.selectedNetworks.length > 0)
      ) {
        updates.selectedNetworks = artCaption.selectedNetworks;
      }
      if (!existing.publicationStatus && artCaption.publicationStatus) {
        updates.publicationStatus = artCaption.publicationStatus;
      }
      if (Object.keys(updates).length === 0) {
        return prev;
      }
      return {...prev, [artKey]: {...existing, ...updates}};
    });
  }, []);

  const updateArtScheduleField = useCallback((artKey, field, value) => {
    setArtScheduleData((prev) => ({
      ...prev,
      [artKey]: {
        ...(prev[artKey] || {}),
        [field]: value,
      },
    }));
  }, []);

  const openSchedulePreview = useCallback(
    (art, index = 0) => {
      const normalizedArt = normalizeSchedulePreviewArt(art);
      ensureArtScheduleEntry(normalizedArt, index);
      setHasSelectedSchedulePublicationItem(true);
      setSchedulePreviewRequest({art: normalizedArt, initialTab: 'edit'});
    },
    [ensureArtScheduleEntry],
  );

  const clearSchedulePreviewSelection = useCallback(() => {
    setHasSelectedSchedulePublicationItem(false);
  }, []);

  const scheduleUx = useMemo(
    () =>
      resolveScheduleUxState({
        messages,
        runtime: campaignRuntimeState,
        socialScheduleContext,
        hasSelectedSchedulePublicationItem,
      }),
    [
      messages,
      campaignRuntimeState,
      socialScheduleContext,
      hasSelectedSchedulePublicationItem,
    ],
  );

  const value = useMemo(
    () => ({
      ...campaignRuntimeState,
      ...socketRuntime,
      viewModel,
      runtime: campaignRuntimeState,
      pendingSubmissions,
      registerPendingSubmission,
      clearPendingSubmission,
      resetCampaignStrategy: resetCampaignRuntime,
      schedulePreviewRequest,
      setSchedulePreviewRequest,
      schedulePreviewArt: schedulePreviewRequest?.art ?? null,
      setSchedulePreviewArt: openSchedulePreview,
      clearSchedulePreviewSelection,
      hasSelectedSchedulePublicationItem,
      scheduleUx,
      artScheduleData,
      setArtScheduleData,
      socialScheduleContext,
      setSocialScheduleContext,
      resetArtScheduleData,
      ensureArtScheduleEntry,
      updateArtScheduleField,
    }),
    [
      campaignRuntimeState,
      socketRuntime,
      viewModel,
      resetCampaignRuntime,
      pendingSubmissions,
      registerPendingSubmission,
      clearPendingSubmission,
      schedulePreviewRequest,
      openSchedulePreview,
      clearSchedulePreviewSelection,
      scheduleUx,
      artScheduleData,
      resetArtScheduleData,
      ensureArtScheduleEntry,
      updateArtScheduleField,
      socialScheduleContext,
    ],
  );

  return (
    <CampaignRuntimeContext.Provider value={value}>
      {children}
    </CampaignRuntimeContext.Provider>
  );
}

export function useCampaignRuntimeContext() {
  const context = useContext(CampaignRuntimeContext);
  if (!context) {
    throw new Error(
      'useCampaignRuntimeContext must be used within CampaignRuntimeProvider',
    );
  }
  return context;
}

/** @deprecated Use useCampaignRuntimeContext */
export const useCampaignStrategy = useCampaignRuntimeContext;

/** @deprecated Use CampaignRuntimeProvider */
export const CampaignStrategyProvider = CampaignRuntimeProvider;

export default CampaignRuntimeProvider;
